import { ChevronRight, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useFeeds, useSchedules } from "../../api/hooks.js";
import { Button } from "../../design-system/button.js";
import { StateSurface } from "../../design-system/surfaces.js";
import { PageHeading } from "../shared/page-heading.js";
import { ScheduleDialog } from "./schedule-dialog.js";
import { feedsUsingSchedule } from "./usage.js";
import { UsedBy } from "./used-by.js";

/**
 * Every schedule, as global objects. Like playlists, they are assigned to feeds and
 * never carved into them, so the users of one are read off the feeds.
 */
export function SchedulesPage() {
  const schedules = useSchedules(true);
  const feeds = useFeeds(true);
  const [creating, setCreating] = useState(false);

  const list = schedules.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeading subtitle="Which playlist plays when." title="Schedules" />
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" />
          New schedule
        </Button>
      </div>

      {schedules.loading ? (
        <StateSurface kind="loading" title="Reading every schedule" />
      ) : schedules.error ? (
        <StateSurface
          action={
            <Button onClick={schedules.reload} size="small" variant="secondary">
              Try again
            </Button>
          }
          kind="error"
          title="The schedules could not be read"
        >
          {schedules.error}
        </StateSurface>
      ) : list.length === 0 ? (
        <StateSurface
          action={
            <Button
              onClick={() => {
                setCreating(true);
              }}
              size="small"
            >
              <Plus aria-hidden="true" />
              New schedule
            </Button>
          }
          kind="empty"
          title="No schedules yet"
        >
          A feed with no schedule plays its default playlist around the clock.
        </StateSurface>
      ) : (
        <ul className="flex flex-col gap-3">
          {list.map((schedule) => (
            <li key={schedule.id}>
              <Link
                className="flex items-center gap-4 rounded-md border border-border bg-surface px-4 py-3.5 transition-colors hover:border-accent-line"
                to={`/schedules/${encodeURIComponent(schedule.id)}`}
              >
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <h2 className="truncate text-sm font-semibold text-ink">
                      {schedule.name}
                    </h2>
                    <span className="rounded-full bg-surface-hi px-2 py-0.5 font-mono text-[0.5625rem] tracking-[0.08em] text-ink-3">
                      {schedule.timezone}
                    </span>
                    {schedule.enabled ? null : (
                      <span className="rounded-full border border-border-hi px-2 py-0.5 font-mono text-[0.5625rem] tracking-[0.08em] text-ink-3 uppercase">
                        Disabled
                      </span>
                    )}
                  </div>
                  <UsedBy
                    emptyLabel="No feed is assigned this yet"
                    uses={feedsUsingSchedule(schedule.id, feeds.data ?? [])}
                  />
                </div>
                <ChevronRight
                  aria-hidden="true"
                  className="size-4 shrink-0 text-ink-3"
                />
              </Link>
            </li>
          ))}
        </ul>
      )}

      <ScheduleDialog
        onClose={() => {
          setCreating(false);
        }}
        onCreated={schedules.reload}
        open={creating}
      />
    </div>
  );
}
